// EarthTiles /v1 client (TIL-1..5, E-38). Network + persistence live here (the sim
// half is `sim/earth/tiles.ts`). The default location (KHFD) boots from the BUNDLED
// fixture with ZERO network (offline-first, ERT-6, mirroring Atlas ATL-6); any other
// location prefetches the FULL extent from a running service at world create — so a
// mid-play tile miss can never happen — and a service failure REFUSES (no fake Earth,
// E-38). Decoded tiles cache in the `tileCache` IDB store keyed by URL (TIL-4).
import { TILE } from '../../sim/config';
import {
  parseMeta, parseDemTile, parseVectorTile, TileContractError, tilesForBbox,
  type TileMeta, type DemTilePayload, type VectorTilePayload, type DemSourceId,
} from '../../sim/earth/tiles';
import { openDB, idbGet, idbPut } from '../storage/idb';

/** Everything an Earth world needs from the tile layer (M40 assembles it into a world). */
export interface EarthTileSet {
  meta: TileMeta;
  dem: DemTilePayload[];
  vector: VectorTilePayload[];
  /** Where the tiles came from — the badge/provenance source (ERT-3). */
  source: 'fixture' | 'service';
}

// ── Bundled KHFD fixture (Vite inlines it → the default location is fully offline) ──
const RAW = import.meta.glob('/e2e/fixtures/earthtiles-khfd/**/*', { query: '?raw', import: 'default', eager: true }) as Record<string, string>;
// Imagery PNGs are binary → inline as base64 data URLs (still zero-network); the sidecar
// json rides the `?raw` glob above. Empty until John's real-NAIP fixture regen (SVC-5).
const IMG_PNG = import.meta.glob('/e2e/fixtures/earthtiles-khfd/**/*.imagery.png', { query: '?inline', import: 'default', eager: true }) as Record<string, string>;

function pickFixture(suffix: string): string[] {
  return Object.keys(RAW).filter((p) => p.endsWith(suffix)).sort().map((p) => RAW[p]!);
}

/** Parse the bundled KHFD tile set (ERT-6). Pure over the inlined strings — no I/O. */
export function loadBundledFixture(): EarthTileSet {
  const metaEntry = Object.keys(RAW).find((p) => p.endsWith('/meta.json'));
  if (!metaEntry) throw new TileContractError('schema', 'bundled KHFD fixture is missing meta.json');
  const meta = parseMeta(JSON.parse(RAW[metaEntry]!));
  const dem = pickFixture('.dem').map((s) => parseDemTile(JSON.parse(s)));
  const vector = pickFixture('.vector').map((s) => parseVectorTile(JSON.parse(s)));
  return { meta, dem, vector, source: 'fixture' };
}

// ── Remote service (non-default locations) ────────────────────────────────────
export interface TileCache {
  get(url: string): Promise<string | undefined>;
  put(url: string, body: string): Promise<void>;
}

/** The `tileCache` IDB store (TAL-4 / TIL-4), keyed by URL, storing the raw payload. */
export function idbTileCache(): TileCache {
  return {
    async get(url) { const db = await openDB(); return idbGet<string>(db, 'tileCache', url); },
    async put(url, body) { const db = await openDB(); await idbPut(db, 'tileCache', url, body); },
  };
}

export interface FetchOpts {
  fetchImpl?: typeof globalThis.fetch;
  cache?: TileCache;
  timeoutMs?: number;
  signal?: AbortSignal;
}

/** GET text with a timeout; a cache hit skips the request (immutable tiles, TIL-4). */
async function getText(url: string, opts: FetchOpts, cacheable: boolean): Promise<string> {
  if (cacheable && opts.cache) { const hit = await opts.cache.get(url); if (hit !== undefined) return hit; }
  const doFetch = opts.fetchImpl ?? globalThis.fetch;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), opts.timeoutMs ?? TILE.TIMEOUT_MS);
  opts.signal?.addEventListener('abort', () => ctrl.abort());
  try {
    const res = await doFetch(url, { signal: ctrl.signal });
    if (!res.ok) throw new TileContractError('unreachable', `tile HTTP ${res.status} for ${url}`);
    const text = await res.text();
    if (cacheable && opts.cache) await opts.cache.put(url, text);
    return text;
  } catch (err) {
    if (err instanceof TileContractError) throw err;
    throw new TileContractError('unreachable', `tile fetch failed for ${url}: ${(err as Error).message}`);
  } finally {
    clearTimeout(timer);
  }
}

const metaUrl = (base: string): string => `${base.replace(/\/$/, '')}/v1/meta`;
const tileUrl = (base: string, kind: string, z: number, x: number, y: number): string => `${base.replace(/\/$/, '')}/v1/${kind}/${z}/${x}/${y}`;

/** Fetch + negotiate `/v1/meta` (major mismatch/schema-invalid → E-38 typed error). */
export async function fetchMeta(baseUrl: string, opts: FetchOpts = {}): Promise<TileMeta> {
  return parseMeta(JSON.parse(await getText(metaUrl(baseUrl), opts, false)));
}

/** Prefetch every DEM + vector tile covering `bbox` from the service (full-extent, so
 *  mid-play misses never happen — ERT-6). Each tile validated (E-38) + cached (TIL-4).
 *  `demSource` picks the DEM zoom. Empty vector tiles 404 and are skipped. */
export async function prefetchExtent(
  baseUrl: string, bbox: readonly [number, number, number, number], demSource: DemSourceId, opts: FetchOpts = {},
): Promise<{ dem: DemTilePayload[]; vector: VectorTilePayload[] }> {
  const dem: DemTilePayload[] = [];
  const vector: VectorTilePayload[] = [];
  const demZ = TILE.DEM_Z[demSource] ?? TILE.DEM_Z['srtm-30m']!;
  for (const { x, y } of tilesForBbox(bbox, demZ)) {
    dem.push(parseDemTile(JSON.parse(await getText(tileUrl(baseUrl, 'dem', demZ, x, y), opts, true))));
  }
  for (const { x, y } of tilesForBbox(bbox, TILE.VECTOR_Z)) {
    try { vector.push(parseVectorTile(JSON.parse(await getText(tileUrl(baseUrl, 'vector', TILE.VECTOR_Z, x, y), opts, true)))); }
    catch (err) { if (!(err instanceof TileContractError && err.kind === 'unreachable')) throw err; } // 404 = empty tile, skip
  }
  return { dem, vector };
}

export interface EarthTileRequest {
  /** The default KHFD location boots from the bundled fixture, zero network (ERT-6). */
  isDefault: boolean;
  /** Tile service base URL (non-default locations). */
  baseUrl?: string;
  /** World bbox [S,W,N,E] to prefetch (non-default). */
  bbox?: readonly [number, number, number, number];
  demSource?: DemSourceId;
}

/** Load one NAIP imagery tile (PNG data URL + sidecar sha) for the imagery bake (OVL-4,
 *  M43): the bundled fixture first (offline, zero-network), else the tile service. Returns
 *  undefined when neither has it (an unbaked/imagery-less world — the common case today).
 *  The sidecar sha is the pin the bake verifies against. */
export async function loadImageryTile(
  baseUrl: string | undefined, z: number, x: number, y: number, opts: FetchOpts = {},
): Promise<{ png: string; sha256: string } | undefined> {
  const rel = `/tiles/${z}/${x}/${y}.imagery`;
  const pngKey = Object.keys(IMG_PNG).find((p) => p.endsWith(`${rel}.png`));
  if (pngKey) {
    const sidecarKey = Object.keys(RAW).find((p) => p.endsWith(`${rel}.json`));
    if (!sidecarKey) return undefined;
    const sidecar = JSON.parse(RAW[sidecarKey]!) as { sha256?: string };
    if (!sidecar.sha256) return undefined;
    return { png: IMG_PNG[pngKey]!, sha256: sidecar.sha256 };
  }
  if (!baseUrl) return undefined;
  try {
    const base = baseUrl.replace(/\/$/, '');
    // Imagery is binary → fetch as a data URL (blob), not text. The sidecar json is text.
    const png = await getImageDataUrl(`${base}/v1/imagery/${z}/${x}/${y}.png`, opts);
    const sidecar = JSON.parse(await getText(`${base}/v1/imagery/${z}/${x}/${y}.png.json`, opts, true)) as { sha256?: string };
    if (!sidecar.sha256) return undefined;
    return { png, sha256: sidecar.sha256 };
  } catch (err) {
    if (err instanceof TileContractError && err.kind === 'unreachable') return undefined; // 404 = no imagery here
    throw err;
  }
}

/** A `data:` URL of a binary image response (blob → FileReader), cache-aware. */
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = () => reject(new Error('image read failed'));
    fr.readAsDataURL(blob);
  });
}

async function getImageDataUrl(url: string, opts: FetchOpts): Promise<string> {
  if (opts.cache) { const hit = await opts.cache.get(url); if (hit !== undefined) return hit; }
  const doFetch = opts.fetchImpl ?? globalThis.fetch;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), opts.timeoutMs ?? TILE.TIMEOUT_MS);
  opts.signal?.addEventListener('abort', () => ctrl.abort());
  try {
    const res = await doFetch(url, { signal: ctrl.signal });
    if (!res.ok) throw new TileContractError('unreachable', `imagery HTTP ${res.status} for ${url}`);
    const dataUrl = await blobToDataUrl(await res.blob());
    if (opts.cache) await opts.cache.put(url, dataUrl);
    return dataUrl;
  } catch (err) {
    if (err instanceof TileContractError) throw err;
    throw new TileContractError('unreachable', `imagery fetch failed for ${url}: ${(err as Error).message}`);
  } finally {
    clearTimeout(timer);
  }
}

/** Resolve a world's tiles (E-38 source policy). Default → bundled fixture offline.
 *  Any other location REQUIRES the service; unreachable/invalid → a clear typed error
 *  (never a fake Earth). Full-extent prefetch at create means no mid-play fetch. */
export async function resolveEarthTiles(req: EarthTileRequest, opts: FetchOpts = {}): Promise<EarthTileSet> {
  if (req.isDefault) return loadBundledFixture();
  if (!req.baseUrl || !req.bbox) throw new TileContractError('unreachable', 'a non-default Earth world needs a tile service URL + bbox');
  const cache = opts.cache ?? idbTileCache();
  const withCache: FetchOpts = { ...opts, cache };
  const meta = await fetchMeta(req.baseUrl, withCache); // negotiates the contract (E-38)
  const { dem, vector } = await prefetchExtent(req.baseUrl, req.bbox, req.demSource ?? 'srtm-30m', withCache);
  if (dem.length === 0) throw new TileContractError('unreachable', 'tile service returned no DEM for this extent');
  return { meta, dem, vector, source: 'service' };
}
